import { Checkbox, Divider, Select, Table, Tooltip } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { ColumnProps } from 'antd/lib/table'
import * as R from 'ramda'
import React from 'react'
import { withFirebase } from 'react-redux-firebase'
import { compose, withProps } from 'recompose'

import { isAdmin } from '../../helpers/auth'
import isDefined from '../../helpers/isDefined'
import { championshipCategoryResults } from '../../helpers/results'
import { playerColumns, rowParDiffClassName } from '../../shared/columns'

import { CategoryId } from '../../schemas/Category'
import { PlayerId } from '../../schemas/Player'
import { TenantId } from '../../schemas/Tenant'
import { ResultsOptionalColumn, Tournament, TournamentId } from '../../schemas/Tournament'

import { Championship } from 'src/schemas/Championship'
import { ChampionshipCategory } from 'src/schemas/ChampionshipCategory'
import { ChampionshipResults } from 'src/schemas/ChampionshipResults'
import './ChampionshipResultsInCategory.css'

const optionalColumn = (
  { screenResultsOptionalColumns = [], printResultsOptionalColumns = [] }: Championship,
  key: ResultsOptionalColumn,
) => (columnProps: ColumnProps<FIXME>): ColumnProps<FIXME> | undefined => {
  const screenEnabled = R.includes(key, screenResultsOptionalColumns)
  const printEnabled = R.includes(key, printResultsOptionalColumns)
  const isEnabled = screenEnabled || printEnabled
  const isJustScreen = screenEnabled && !printEnabled
  const isJustPrint = printEnabled && !screenEnabled

  const makeClassName = (newClassName: string) =>
    columnProps.className ? `${columnProps.className} ${newClassName}` : newClassName

  if (isEnabled) {
    if (isJustScreen) {
      return {
        ...columnProps,
        className: makeClassName('justScreen'),
      }
    }

    if (isJustPrint) {
      return {
        ...columnProps,
        className: makeClassName('justPrint'),
      }
    }

    return columnProps
  }

  return undefined
}

const getResultsInCategoryColumns = (
  tenant: TenantId,
  championship: Championship,
  categoryId: CategoryId,
  tournaments: Tournament[],
  admin: boolean,
  setPlayoffFlag: (playerId: PlayerId, round: number) => (playoffFlag: boolean) => void,
  setPlayoffRank: (playerId: PlayerId, round: number) => (playoffRank: number) => void,
) => {
  const getLastRoundPlayoffFlag = R.path<boolean>(['lastRoundPlayoffFlag', categoryId])

  return [
    {
      title: 'rank',
      width: '4em',
      dataIndex: 'rank',
    },
    {
      title: 'Player',
      width: '20em',
      key: 'fullName',
      render: (text, record) => (
        <div style={{ textAlign: 'left' }}>
          {record.firstName} {record.lastName}
        </div>
      ),
    },
    optionalColumn(
      championship,
      ResultsOptionalColumn.Nationality,
    )({
      ...playerColumns.nationality,
      width: '6em',
    }),
    optionalColumn(
      championship,
      ResultsOptionalColumn.Club,
    )({
      ...playerColumns.club,
      width: '6em',
    }),
    ...tournaments.map((tournament) => ({
      title: tournament.shortName ? (
        <Tooltip title={tournament.name}>{tournament.shortName}</Tooltip>
      ) : (
        tournament.name
      ),
      width: '2em',
      render: (text, record) => {
        const droppedTournaments: TournamentId[] = R.take(
          championship.dropTournamentsCount ?? 0,
          R.pathOr([], ['sortedTournaments', categoryId], record),
        )

        const result = R.pathOr('-', ['points', categoryId, tournament.id], record)

        return R.includes(tournament.id, droppedTournaments) ? (
          <span className="strikethrough" style={{ display: 'inline-block', width: '50%' }}>
            {result}
          </span>
        ) : (
          result
        )
      },
    })),
    {
      title: 'SUM',
      dataIndex: 'totalScore',
      width: '4em',
      className: 'ResultsCol--sum',
      render: (text, record) => R.pathOr(0, ['totals', categoryId], record),
    },
    ...(admin
      ? [
          {
            title: 'Playoff',
            render: (text, record, index) => {
              const playoffFlagOnChangeHandler = (event: CheckboxChangeEvent) => {
                event.preventDefault()
                setPlayoffFlag(record.id, record.lastRound)(event.target.checked)
              }

              return (
                (record.playoffRankPlayers.length > 1 || getLastRoundPlayoffFlag(record)) && (
                  <Checkbox
                    checked={getLastRoundPlayoffFlag(record)}
                    onChange={playoffFlagOnChangeHandler}
                  />
                )
              )
            },
            width: '2em',
          },
          {
            title: 'Rank',
            render: (text, record, index) => {
              const playoffRankChangeHandler = (value: any) => {
                setPlayoffRank(record.id, record.lastRound)(value)
              }

              return (
                getLastRoundPlayoffFlag(record) && (
                  <Select
                    size="small"
                    value={R.path(['lastRoundPlayoffRank', categoryId], record)}
                    onChange={playoffRankChangeHandler}>
                    {R.range(1, record.playoffRankPlayers.length + 1).map((playoffRank) => (
                      <Select.Option key={playoffRank} value={playoffRank}>
                        {playoffRank}
                      </Select.Option>
                    ))}
                  </Select>
                )
              )
            },
            width: '2em',
          },
        ]
      : []),
    {
      title: 'Notes',
      width: '4em',
      render: (text, record, index) => {
        const dnfFlag = record.isDisqualified && 'DNF'
        const penaltyFlag = R.pathOr(0, ['penalties'], record) > 0 && `⚠️ ${record.penalties}`
        const playoffFlag = getLastRoundPlayoffFlag(record) && 'P'

        const flags = [dnfFlag, penaltyFlag, playoffFlag].filter(Boolean)

        return R.intersperse(<Divider type="vertical" />, flags)
      },
    },
  ].filter(isDefined)
}

type ResultsInCategoryOuterProps = {
  dataIsLoaded: boolean
  tenant: TenantId
  championship?: Championship
  category: ChampionshipCategory
  tournaments: Tournament[]
  results: ChampionshipResults[]
}

type ResultsInCategoryInnerProps = ResultsInCategoryOuterProps &
  WithPlayoffHandlers & {
    championship?: Championship
    category?: ChampionshipCategory
    isMain: boolean
    results?: ChampionshipResults[]
    profile: any // TODO
    dataSource: any[] // TODO
  }

type ResultsInCategoryDefaultProps = {
  onCycleComplete(): void
}

class ResultsInCategory extends React.Component<
  ResultsInCategoryInnerProps & ResultsInCategoryDefaultProps
> {
  render() {
    const {
      dataSource,
      profile,
      dataIsLoaded,
      isMain,
      tenant,
      championship,
      category,
      tournaments,
      setPlayoffFlag,
      setPlayoffRank,
    } = this.props

    const rowClassName = (record: any /* TODO */, index: number) => rowParDiffClassName(record)

    const tableTitle = () => category['name:en'] // TODO take from locale

    const displayNotes = isMain && championship?.notes

    const tableFooter = displayNotes ? () => championship?.notes : undefined

    return (
      <Table
        rowKey="id"
        className="ResultsInCategory"
        title={tableTitle}
        loading={!dataIsLoaded}
        bordered
        dataSource={dataSource}
        columns={
          championship &&
          getResultsInCategoryColumns(
            tenant,
            championship,
            category.id,
            tournaments,
            isAdmin(tenant, profile),
            setPlayoffFlag,
            setPlayoffRank,
          )
        }
        footer={tableFooter}
        rowClassName={rowClassName}
        pagination={false}
      />
    )
  }
}

type WithPlayoffHandlers = {
  setPlayoffFlag(playerId: PlayerId, round: number): (playoffFlag: boolean) => Promise<any>
  setPlayoffRank(playerId: PlayerId, round: number): (playoffRank: number) => Promise<any>
}

// adds props related to `Category`
const addCategoryProps = ({ category }) => ({
  isMain: category?.main,
})

const addDataSource = ({
  championship,
  tournaments,
  category,
  results,
}: {
  championship?: Championship
  tournaments?: Tournament[]
  category?: ChampionshipCategory
  results: ChampionshipResults[]
}) => {
  const resultsInCategory: ChampionshipResults[] = (results || []).filter(
    (result) => category && result.points.hasOwnProperty(category.id),
  )

  const dataSource =
    championship && category
      ? championshipCategoryResults(
          championship,
          (tournaments || []).filter(
            (tournament) =>
              tournament.championshipId === championship.id &&
              R.any(
                // drop tournaments that have no published results
                (resultInCategory) =>
                  R.path(['points', category.id, tournament.id], resultInCategory) !== undefined,
                resultsInCategory,
              ),
          ),
          category,
          resultsInCategory,
        )
      : []

  return {
    dataSource,
  }
}

export default compose<ResultsInCategoryInnerProps, ResultsInCategoryOuterProps>(
  withFirebase,
  withProps(addCategoryProps),
  withProps(addDataSource),
  /*
  withHandlers({
    onSetPlayoffFlagError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff flag'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    onSetPlayoffRankError: ({ dispatch }) => (err) => {
      const msg = 'Failed to set playoff rank'
      // tslint:disable-next-line:no-console
      console.error(msg, err)
      dispatch(notificationActions.notifyFailure(msg))
    },
    saveResult: ({ firestore, tenant, championshipId }) => (playerId: PlayerId) => (
      result: ChampionshipResults,
    ) =>
      firestore.set(
        {
          collection: fsCollections.championshipResults(tenant, championshipId),
          doc: playerId,
        },
        result,
      ),
  }),
  withHandlers<FIXME, WithPlayoffHandlers>({
    setPlayoffFlag: ({ saveResult, categoryId, tournamentResultsMap, onSetPlayoffFlagError }) => (
      playerId: PlayerId
    ) => (playoffFlag: boolean) => {
      const result = tournamentResultsMap[key] || emptyResult(playerId, round)

      return saveResult(
        playerId,
        round,
      )(
        (R.compose(
          R.set(R.lensPath(['playoffRank', categoryId]), playoffFlag ? 1 : null),
          R.set(R.lensPath(['playoffFlag', categoryId]), playoffFlag) as FIXME,
        ) as FIXME)(result),
      ).catch(onSetPlayoffFlagError)
    },
    setPlayoffRank: ({ saveResult, categoryId, tournamentResultsMap, onSetPlayoffRankError }) => (
      playerId: PlayerId,
      round: number,
    ) => (rank: number) => {
      const key = resultKey(playerId, round)
      const result = tournamentResultsMap[key] || emptyResult(playerId, round)

      return saveResult(
        playerId,
        round,
      )(
        (R.compose(
          R.set(R.lensPath(['playoffRank', categoryId]), rank),
          R.set(R.lensPath(['playoffFlag', categoryId]), true) as FIXME,
        ) as FIXME)(result),
      ).catch(onSetPlayoffRankError)

    },
  }),
    */
)(ResultsInCategory)



// WEBPACK FOOTER //
// ./src/components/championship/ChampionshipResultsInCategory.tsx